import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { resolveLandingPath } from "@/app/auth/resolveNavAccess";
import { useNavContext } from "@/app/auth/useNavContext";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaPageHeader } from "@/shared/components/layout/ColocaPageHeader";
import useTranslations from "@/shared/lang/useTranslations";

import { useVerificationEngine } from "../hooks/useVerificationEngine";
import { useVerificationSocket } from "../hooks/useVerificationSocket";
import { useVerificationStatusPolling } from "../hooks/useVerificationStatusPolling";
import { invalidateAccountVerification } from "../services/accountVerification.memo";
import { VerificationService } from "../services/verificationService";
import type {
  CreateVerificationResponse,
  KycLevel,
  KycStatus,
  VerificationPurpose,
  VerificationStatusResponse,
} from "../types/verification-api.types";
import { CaptureStep } from "./CaptureStep";
import { ConsentGate } from "./ConsentGate";
import { KybCompanyStep } from "./KybCompanyStep";
import { KycPersonStep } from "./KycPersonStep";
import { KycStatusPill } from "./KycStatusPill";
import { OnboardingVerificationWelcome } from "./OnboardingVerificationWelcome";
import { ProcessingScreen } from "./ProcessingScreen";
import { ResultScreen } from "./ResultScreen";
import { StepIntroCard } from "./StepIntroCard";
import { VerificationLayout } from "./VerificationLayout";
import { VerificationStepper } from "./VerificationStepper";

export interface OnboardingVerificationFlowProps {
  /** KYC (natural person) or KYB (company) — chosen by the organization, or read off its corporate identity. */
  purpose: VerificationPurpose;
  /** The corporate identity to resume from, when it already exists. */
  identityId?: string | null;
  /** Its verification status as the account endpoint reported it, for the header while the rich status loads. */
  initialStatus?: KycStatus | null;
}

/**
 * Marco A — the authenticated onboarding engine, parametrized by purpose. KYB: intro → consent → company
 * (`KybCompanyStep`) → company capture → processing → result. KYC: intro → consent → person data
 * (`KycPersonStep`) → document + selfie capture → processing → result. Both create the organization's
 * corporate identity (`is_organization_identity: true`) and resume from it when it already exists.
 *
 * Every status change invalidates the account-verification memo, so the Wallets banner, Settings and the
 * dispersions gate re-read on their next mount.
 */
export function OnboardingVerificationFlow({
  purpose,
  identityId: initialIdentityId,
  initialStatus,
}: OnboardingVerificationFlowProps) {
  const { t } = useTranslations("verification");
  const navigate = useNavigate();
  const isKyc = purpose === "KYC";
  const captureScreen = isKyc ? "capture-doc" : "capture";

  const engine = useVerificationEngine({ purpose, frame: "onboarding" });
  const { goTo, applyStatus } = engine;

  const [identityId, setIdentityId] = useState<string | null>(initialIdentityId ?? null);
  const [lastStatus, setLastStatus] = useState<VerificationStatusResponse | null>(null);

  // Where this user can actually land. Not `/app/wallets`: this flow declares no `requires`, so a member
  // without `payments:read` reaches it and would be sent straight into a NoAccessView. Both the header's
  // "continue later" and the result's CTA read this one value, so they cannot disagree — and when it is
  // null the user has nowhere to go and neither control is offered, rather than offering a dead end.
  const navContext = useNavContext();
  const landingPath = navContext ? resolveLandingPath(navContext) : null;

  // Resume from an existing corporate identity: PENDING → capture, anything else → its result.
  useEffect(() => {
    if (!initialIdentityId) return;
    let cancelled = false;
    VerificationService.getStatus(initialIdentityId)
      .then(({ data: status }) => {
        if (cancelled) return;
        setLastStatus(status);
        if (status.status === "PENDING") goTo(captureScreen);
        else applyStatus(status.status);
      })
      .catch(() => {
        // Could not read the rich status — fall back to what the account endpoint said.
        if (cancelled) return;
        if (initialStatus && initialStatus !== "PENDING") applyStatus(initialStatus);
        else goTo(captureScreen);
      });
    return () => {
      cancelled = true;
    };
  }, [initialIdentityId, initialStatus, captureScreen, goTo, applyStatus]);

  // Leaving the page is news too: whatever happened here, the other surfaces must re-read.
  useEffect(() => () => invalidateAccountVerification(), []);

  const pollingEnabled =
    Boolean(identityId) && (engine.screen === "processing" || engine.result === "review");
  useVerificationStatusPolling(
    async () => (await VerificationService.getStatus(identityId as string)).data,
    pollingEnabled,
    (data) => {
      setLastStatus(data);
      applyStatus(data.status);
      invalidateAccountVerification();
    },
  );

  // Preferred channel: real-time push (the polling above stays as fallback).
  useVerificationSocket(identityId, (payload) => {
    const status = payload.status as KycStatus;
    setLastStatus({
      status,
      level: (payload.level ?? "NN") as KycLevel,
      rejection_reason: payload.rejection_reason,
    });
    applyStatus(status);
    invalidateAccountVerification();
  });

  const handleCreated = useCallback(
    (response: CreateVerificationResponse) => {
      setIdentityId(response.identity.id);
      invalidateAccountVerification();
      goTo(captureScreen);
    },
    [goTo, captureScreen],
  );

  // Invalidate before leaving, not only on unmount: the banner, Settings and the dispersions gate must read
  // the new status on the page we are navigating TO, and the unmount runs mid-navigation.
  const handleGoHome = useCallback(() => {
    if (!landingPath) return;
    invalidateAccountVerification();
    navigate(landingPath);
  }, [landingPath, navigate]);

  // Same invalidation for every way out of the approved welcome.
  const handleLeaveTo = useCallback(
    (href: string) => {
      invalidateAccountVerification();
      navigate(href);
    },
    [navigate],
  );

  const renderScreen = () => {
    switch (engine.screen) {
      case "intro":
        return <StepIntroCard purpose={purpose} onStart={() => goTo("consent")} />;
      case "consent":
        return <ConsentGate purpose={purpose} onAccept={() => goTo(isKyc ? "data" : "company")} />;
      case "data":
      case "confirm":
        return <KycPersonStep onCreated={handleCreated} />;
      case "company":
      case "company-verified":
        return <KybCompanyStep onCreated={handleCreated} />;
      case "capture-doc":
      case "capture":
      case "submit":
        return (
          <CaptureStep
            start={() => VerificationService.startCapture(identityId as string).then((r) => r.data)}
            mode={isKyc ? "document" : "company"}
            onComplete={() => goTo("processing")}
          />
        );
      case "processing":
        return <ProcessingScreen purpose={purpose} />;
      case "result": {
        // Approved: the welcome, with the places the approval opened. It does not auto-advance — a menu of
        // choices that carries the reader away on its own would take the choice from them.
        if (engine.result === "verified") {
          return (
            <OnboardingVerificationWelcome
              purpose={purpose}
              identityId={identityId}
              onNavigate={handleLeaveTo}
            />
          );
        }
        const canRetry = engine.result === "rejected" || engine.result === "expired";
        return (
          <ResultScreen
            result={engine.result ?? "review"}
            rejectionReason={lastStatus?.rejection_reason}
            onPrimary={canRetry ? () => goTo(captureScreen) : undefined}
          />
        );
      }
      default:
        return <ProcessingScreen purpose={purpose} />;
    }
  };

  return (
    <div className="space-y-6">
      <ColocaPageHeader
        title={t("onboarding_title")}
        description={t("onboarding_description")}
        actions={
          <>
            <KycStatusPill status={lastStatus?.status ?? initialStatus ?? "PENDING"} />
            {landingPath ? (
              <ColocaButton variant="ghost" onClick={handleGoHome}>
                {t("button_continue_later")}
              </ColocaButton>
            ) : null}
          </>
        }
      />

      <VerificationLayout
        // The approved welcome lays its shortcuts out in two columns, which the person-sized card cannot fit.
        size={isKyc && !(engine.screen === "result" && engine.result === "verified") ? "person" : "business"}
        stepper={
          engine.screen === "intro" ? undefined : (
            <VerificationStepper purpose={purpose} currentStep={engine.step} totalSteps={engine.totalSteps} />
          )
        }
      >
        {renderScreen()}
      </VerificationLayout>
    </div>
  );
}
